<template>
  <div class="booking-overview" aria-label="Overview">
    <div class="booking-overview__main">
      <section class="booking-overview__panel" aria-labelledby="booking-itinerary-title">
        <header class="booking-overview__panel-header">
          <h2 id="booking-itinerary-title">Itinerary</h2>
          <span>{{ flights.length }} {{ flights.length === 1 ? 'flight' : 'flights' }}</span>
        </header>
        <div v-if="aircraftError" class="booking-overview__images-error" role="status">
          <span>Aircraft images unavailable.</span>
          <button type="button" :disabled="aircraftLoading" title="Retry aircraft images" aria-label="Retry aircraft images" @click="loadAircraft"><i class="pi pi-refresh" aria-hidden="true" /></button>
        </div>
        <ol v-if="flights.length" class="booking-overview__flights" aria-label="Flight segments">
          <flight-segment-row
            v-for="(flight, index) in flights"
            :key="flight.id || index"
            orientation="vertical"
            :date="flight.date"
            :origin="flight.from"
            :destination="flight.to"
            :departure="flight.departure"
            :arrival="flight.arrival"
            :flight-number="flight.flight_number"
            :via="flight.via"
            :aircraft="flight.aircraft"
            :aircraft-image="aircraftImage(flight.aircraft)"
            :baggage-allowance="flight.baggage_allowance"
            :infant-baggage-allowance="flight.infant_baggage_allowance"
            :show-infant-baggage="hasInfants"
          />
        </ol>
        <p v-else class="booking-overview__empty">No flight segments available.</p>
      </section>
      <booking-passengers :booking="booking" :refresh-key="passengersRefreshKey" @assignment-change="$emit('assignment-change', $event)" />
    </div>
    <aside class="booking-overview__sidebar">
      <section v-if="showTimeLimits" class="booking-overview__panel" aria-labelledby="booking-time-limits-title">
        <header class="booking-overview__panel-header">
          <h2 id="booking-time-limits-title">Time limits</h2>
          <app-button v-if="canEditLimits" ref="editLimits" class="booking-overview__invoice-edit" theme="empty" title="Edit time limits" aria-label="Edit time limits" @click.native="openEdit('time-limits')"><i class="pi pi-pencil" aria-hidden="true" /></app-button>
        </header>
        <dl class="booking-overview__deadlines">
          <div v-for="limit in deadlines" :key="limit.key" :data-deadline-state="deadlineState(booking[limit.key], now)">
            <dt>{{ limit.name }}</dt>
            <dd><time v-if="dateTime(booking[limit.key])" :datetime="booking[limit.key]">{{ dateTime(booking[limit.key]) }}</time><span v-else>—</span></dd>
          </div>
        </dl>
      </section>
      <section v-if="showTicket" class="booking-overview__panel" aria-labelledby="booking-ticket-title">
        <header class="booking-overview__panel-header">
          <h2 id="booking-ticket-title">Ticket</h2>
          <app-button v-if="canEditTicket" ref="editTicket" class="booking-overview__invoice-edit" theme="empty" title="Edit ticket" aria-label="Edit ticket" @click.native="openEdit('ticket')"><i class="pi pi-pencil" aria-hidden="true" /></app-button>
        </header>
        <dl class="booking-overview__invoice-fields">
          <div><dt>Number</dt><dd>{{ empty(booking.ticket_number) ? '—' : booking.ticket_number }}</dd></div>
          <div><dt>Attachment</dt><dd><a v-if="booking.ticket && booking.ticket.file" class="booking-overview__invoice-link" :href="booking.ticket.file" target="_blank" rel="noopener noreferrer"><i class="pi pi-external-link" aria-hidden="true" /> View ticket</a><span v-else>—</span></dd></div>
        </dl>
      </section>
      <section class="booking-overview__panel" aria-labelledby="booking-invoice-title" :aria-busy="invoiceLoading">
        <header class="booking-overview__panel-header">
          <h2 id="booking-invoice-title">Invoice <span v-if="invoice && !empty(invoice.version)" class="booking-overview__invoice-version">(v{{ invoice.version }})</span></h2>
          <div class="booking-overview__invoice-actions">
            <span v-if="invoiceLoading && invoice" role="status" aria-label="Updating invoice"><i class="pi pi-spin pi-spinner" aria-hidden="true" /></span>
            <app-button v-if="canEditInvoice && invoice" ref="editInvoice" class="booking-overview__invoice-edit" theme="empty" title="Edit invoice" aria-label="Edit invoice" :disabled="invoiceLoading || Boolean(invoiceError)" @click.native="openInvoiceModal"><i class="pi pi-pencil" aria-hidden="true" /></app-button>
          </div>
        </header>
        <div class="booking-overview__invoice-content">
          <app-block-loader v-if="invoiceLoading && !invoice" min-height="380px" :rows="5">Loading invoice…</app-block-loader>
          <div v-if="invoiceError" class="booking-overview__invoice-error" role="alert">
            <span>{{ invoiceError }}</span>
            <app-button theme="dark" size="small" @click.native="loadInvoice">Retry</app-button>
          </div>
          <template v-if="invoice">
            <dl class="booking-overview__invoice-fields">
              <div><dt>Estimated</dt><dd>{{ invoiceAmount(invoice.estimated_amount) }}</dd></div>
              <div><dt>Number</dt><dd>{{ empty(invoice.invoice_number) ? '—' : invoice.invoice_number }}</dd></div>
              <div>
                <dt>Attachment</dt>
                <dd>
                  <a v-if="invoice.attachment && invoice.attachment.file" class="booking-overview__invoice-link" :href="invoice.attachment.file" target="_blank" rel="noopener noreferrer"><i class="pi pi-external-link" aria-hidden="true" /> View invoice</a>
                  <span v-else>—</span>
                </dd>
              </div>
              <div><dt>Total Amount</dt><dd>{{ invoiceAmount(invoice.total_amount) }}</dd></div>
              <div><dt>Outstanding Amount</dt><dd>{{ invoiceAmount(invoice.outstanding_amount) }}</dd></div>
            </dl>
            <footer class="booking-overview__invoice-updated">
              <span>Updated At:</span>
              <time v-if="dateTime(invoice.balance_updated_at)" :datetime="invoice.balance_updated_at">{{ dateTime(invoice.balance_updated_at) }}</time>
              <span v-else>—</span>
            </footer>
          </template>
        </div>
      </section>
    </aside>
    <booking-edit-modal v-for="block in ['time-limits', 'ticket']" :key="block" :name="editModalName(block)" :booking="booking" :block="block" @updated="$emit('updated', $event)" @closed="focusEditButton(block)" />
    <booking-invoice-modal v-if="isManual && invoice" :name="invoiceModalName" :booking="booking" :invoice="invoice" @updated="updateInvoice" @closed="focusInvoiceButton" />
  </div>
</template>

<script>
import FlightSegmentRow from '@/components/flights/FlightSegmentRow.vue'
import BookingPassengers from './Passengers.vue'
import BookingEditModal from './BookingEditModal.vue'
import { canEditBookingBlock } from './booking-edits'
import BookingInvoiceModal from './BookingInvoiceModal.vue'
import { getFlightsAircraft, getFlightsBookingInvoice } from '@/http'
import { code, dateTime, deadlineState, empty, responseError, rows } from './booking-detail'

export default {
  name: 'FlightBookingOverview',
  components: { FlightSegmentRow, BookingPassengers, BookingInvoiceModal, BookingEditModal },
  props: { booking: { type: Object, required: true }, passengersRefreshKey: { type: Number, default: 0 } },
  data() {
    return {
      now: Date.now(),
      timer: null,
      aircraft: [],
      aircraftLoaded: false,
      aircraftLoading: false,
      aircraftError: '',
      aircraftRequestId: 0,
      invoice: null,
      invoiceLoading: false,
      invoiceError: '',
      invoiceRequestId: 0,
      deadlines: [
        { key: 'payment_time_limit', name: 'Payment · PTL' },
        { key: 'ticketing_time_limit', name: 'Ticketing · TTL' }
      ]
    }
  },
  computed: {
    showTimeLimits() { return code(this.booking.status) !== 'ticketed' },
    showTicket() { return ['confirmed', 'ticketed'].includes(code(this.booking.status)) },
    canEditLimits() { return canEditBookingBlock(this.booking, this.$store.state.userGroups, 'time-limits') },
    canEditTicket() { return canEditBookingBlock(this.booking, this.$store.state.userGroups, 'ticket') },
    canEditInvoice() { return canEditBookingBlock(this.booking, this.$store.state.userGroups, 'invoice') },
    bookingId() { return this.booking.id },
    isManual() { return code(this.booking.booking_mode) === 'manual' },
    invoiceModalName() { return `edit-booking-invoice-${this.booking.id}` },
    flights() { return Array.isArray(this.booking.flights) ? this.booking.flights : [] },
    hasInfants() { return Number(this.booking.infant) > 0 },
    aircraftIds() { return this.flights.filter(flight => flight.aircraft && flight.aircraft.id).map(flight => flight.aircraft.id).join(',') }
  },
  watch: {
    bookingId: { immediate: true, handler() { this.invoice = null; this.loadInvoice() } },
    passengersRefreshKey() { this.loadInvoice() },
    aircraftIds: {
      immediate: true,
      handler() { if (!this.aircraftLoaded && !this.aircraftLoading) this.loadAircraft() }
    }
  },
  mounted() { this.timer = setInterval(() => { this.now = Date.now() }, 60000) },
  beforeDestroy() {
    clearInterval(this.timer)
    this.aircraftRequestId++
    this.invoiceRequestId++
  },
  methods: {
    dateTime,
    deadlineState,
    empty,
    editModalName(block) { return `edit-booking-${block}-${this.booking.id}` },
    openEdit(block) { if (canEditBookingBlock(this.booking, this.$store.state.userGroups, block)) this.$modal.show(this.editModalName(block)) },
    focusEditButton(block) { const button = this.$refs[block === 'ticket' ? 'editTicket' : 'editLimits']; if (button) button.$el.focus() },
    openInvoiceModal() {
      if (this.canEditInvoice && this.invoice && !this.invoiceLoading && !this.invoiceError) this.$modal.show(this.invoiceModalName)
    },
    focusInvoiceButton() { if (this.$refs.editInvoice) this.$refs.editInvoice.$el.focus() },
    updateInvoice(invoice) {
      if (!invoice || invoice.booking_id !== this.booking.id) return
      this.invoiceRequestId++
      this.invoice = invoice
      this.invoiceLoading = false
      this.invoiceError = ''
    },
    invoiceAmount(value) {
      if (empty(value) || !Number.isFinite(Number(value))) return '—'
      return `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value))}$`
    },
    async loadInvoice() {
      const requestId = ++this.invoiceRequestId
      this.invoiceError = ''
      this.invoiceLoading = false
      if (!this.booking.id) return
      this.invoiceLoading = true
      try {
        const { data } = await getFlightsBookingInvoice(this.booking.id, { localErrors: true })
        if (requestId !== this.invoiceRequestId) return
        if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid invoice response')
        this.invoice = data
      } catch (error) {
        if (requestId === this.invoiceRequestId) this.invoiceError = responseError(error, 'Unable to load invoice')
      } finally {
        if (requestId === this.invoiceRequestId) this.invoiceLoading = false
      }
    },
    aircraftImage(aircraft) {
      const entry = aircraft && this.aircraft.find(item => item.id === aircraft.id)
      return (entry && entry.image_url) || ''
    },
    async loadAircraft() {
      if (!this.aircraftIds || this.aircraftLoading) return
      const requestId = ++this.aircraftRequestId
      this.aircraftLoading = true
      this.aircraftError = ''
      try {
        const { data } = await getFlightsAircraft()
        if (requestId !== this.aircraftRequestId) return
        this.aircraft = rows(data)
        this.aircraftLoaded = true
      } catch (error) {
        if (requestId === this.aircraftRequestId) this.aircraftError = responseError(error, 'Unable to load aircraft')
      } finally {
        if (requestId === this.aircraftRequestId) this.aircraftLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-overview { display: grid; grid-template-columns: minmax(0, 1fr) 320px; align-items: start; gap: 20px; }
.booking-overview__main { display: grid; gap: 20px; min-width: 0; }
.booking-overview__panel { min-width: 0; overflow: hidden; border: 1px solid #dce0d6; border-radius: 8px; background: #fff; }
.booking-overview__panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 18px;
  border-bottom: 1px solid #dce0d6;
  background: #fafafa;
  h2 { margin: 0; font-size: 16px; font-weight: 500; }
  > span { color: #62685d; font-size: 14px; }
}
.booking-overview__flights { container-type: inline-size; container-name: booking-itinerary; list-style: none; margin: 0; padding: 0 18px 18px; }
.booking-overview__images-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px 0;
  color: #62685d;
  font-size: 13px;
  button { display: grid; place-items: center; flex-shrink: 0; width: 28px; height: 28px; padding: 0; border: 0; border-radius: 4px; background: #f7f8f5; color: #527b12; cursor: pointer; }
  button:focus-visible { outline: 2px solid #527b12; outline-offset: 2px; }
  button:disabled { opacity: .5; cursor: default; }
}
.booking-overview__sidebar { display: grid; gap: 20px; min-width: 0; }
.booking-overview__invoice-version { color: #62685d; font-size: 14px; font-weight: 400; }
.booking-overview__invoice-actions { display: flex; align-items: center; gap: 8px; }
.booking-overview__invoice-edit.btn {
  width: 32px;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  border: 1px solid $color-border;
  border-radius: 6px;
  background: #fff;
  color: #20241d;
  &:hover:not(:disabled) { background: #f7f8f5; }
  &:focus-visible { outline: 2px solid $color-theme; outline-offset: 2px; }
  &:disabled { opacity: .5; }
}
.booking-overview__invoice-content { min-height: 380px; }
.booking-overview__invoice-fields {
  display: grid;
  gap: 16px;
  margin: 0;
  padding: 18px;
  dt { color: #62685d; margin-bottom: 4px; font-size: 14px; }
  dd { margin: 0; font-weight: 500; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
}
.booking-overview__invoice-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid #dce0d6;
  border-radius: 6px;
  color: #20241d;
  font-size: 13px;
  text-decoration: none;
  &:hover { background: #fafafa; }
  &:focus-visible { outline: 2px solid #527b12; outline-offset: 2px; }
}
.booking-overview__invoice-updated { display: flex; flex-wrap: wrap; gap: 4px 8px; padding: 14px 18px; border-top: 1px solid #dce0d6; background: #fafafa; color: #62685d; font-size: 13px; }
.booking-overview__invoice-error { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; padding: 18px; color: #b03932; }
.booking-overview__empty { margin: 0; padding: 18px; color: #62685d; }
.booking-overview__deadlines {
  display: grid;
  gap: 16px;
  margin: 0;
  padding: 18px;
  dt { color: #62685d; margin-bottom: 4px; font-size: 14px; }
  dd { margin: 0; font-weight: 500; font-variant-numeric: tabular-nums; }
  [data-deadline-state='due-soon'] time { color: #b8860b; }
  [data-deadline-state='expired'] time { color: #b03932; }
}
@media (max-width: 800px) {
  .booking-overview { grid-template-columns: minmax(0, 1fr); }
}
</style>
